import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { MutationObserverOptions } from '@tanstack/vue-query';
import type { RouteLocationRaw } from 'vue-router';
import type { AxiosError } from 'axios';
import { useStore } from '@/store/useStore';
import { mergeMutationOptions, mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import { DOCUMENTS_QUERY } from '@/modules/documents/composableKeys';
import type { Answer } from '@/types/models/answer';
import type { ScopingAnswer } from '@/types/models/scopingAnswer';
import type { QuickAnswerSuggestion } from '@/types/models/quickAnswer';
import { useGenerateControlsWithAnswers } from '@/composables/controls-with-answers';
import { useFramework, useCombinedFrameworkConfig } from '@/modules/framework/composables';
import { useOwnProducts } from '@/modules/products/composables';
import useFeatureFlags, { FeatureFlag } from '@/composables/feature-flags';
import { ReassessmentTriggerType, type ReassessmentStatus } from '@/types/models/reassessment';
import {
  getConfirmedControlsWithAnswers,
  isApplicableControl,
  isControlConfirmed,
} from '@/selectors/controls-with-answers/controlsWithAnswers';
import type { Domain } from '@/types/models/domain';
import { CLIENT_ASSESSMENT_ANSWERS_QUERY } from '@/modules/profile/composableKeys';
import api from './api';
import type { ControlAnswersResponse } from './api/assessment';
import type { ConfirmationResponse } from './api/reassessment';
import {
  ASSESSMENT_ANSWERS_QUERY,
  NEW_IN_SCOPE_DOMAINS_QUERY,
  REASSESMENT_STATUS_QUERY,
} from './composableKeys';
import { useScopingAnswers } from './composables/composables';

// ASSESSMENTS

export function useControlAnswers(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ASSESSMENT_ANSWERS_QUERY],
      async queryFn() {
        const res = await api.getControlAnswers();
        return res.data;
      },
    }),
  );

  const { dependentControls, controlWithID } = useFramework();

  // ALWAYS use this instead of query.data, as this has an eventual consistency fix
  const controlAnswers = computed(
    () =>
      query.data.value?.map((answer) => {
        const control = controlWithID(answer.controlID);

        // This horrific code is to work around an eventual consistency issue
        // on updating a control where child controls might not have been
        // correctly marked as not applicable yet as it happens on an event
        if (
          control.parentID &&
          dependentControls.value[control.parentID]?.includes(control.controlID)
        ) {
          const parentAnswer = query.data.value?.find((otherAnswer) => {
            if (answer.productID) {
              return (
                otherAnswer.controlID === control.parentID &&
                otherAnswer.productID === answer.productID
              );
            }

            return otherAnswer.controlID === control.parentID && !otherAnswer.productID;
          });

          if (!parentAnswer) return answer;

          let notApplicable: boolean;
          try {
            const parsedAnswer = JSON.parse(parentAnswer.answer);

            // answer is stringified JSON e.g. for YesCompliantWithExpiryControl control type
            notApplicable = control.ifParentAnswer !== parsedAnswer.answer;
          } catch {
            // answer is not stringified JSON
            notApplicable = control.ifParentAnswer !== parentAnswer.answer;
          }

          return { ...answer, notApplicable };
        }
        return answer;
      }) || [],
  );

  /** @deprecated use controlAnswersByControlID instead */
  const orgAnswerOnlyByControlID = computed(() =>
    Object.fromEntries(
      controlAnswers.value
        .filter((answer) => !answer.productID)
        .map((answer) => [answer.controlID, answer]),
    ),
  );

  const controlAnswersByControlID = computed(() =>
    Object.groupBy(controlAnswers.value, (answer) => answer.controlID),
  );

  // Sync with semi-deprecated store
  const store = useStore();
  watchEffect(() => {
    if (store) {
      store.commit('assessment/SET_CONTROL_ANSWERS', orgAnswerOnlyByControlID.value);
    }
  });

  function getByAnswerID(answerID: string) {
    for (const controlAnswer of controlAnswers.value) {
      if (controlAnswer.answerID === answerID) {
        return controlAnswer;
      }
    }

    return null;
  }

  return {
    ...query,
    controlAnswers,
    orgAnswerOnlyByControlID,
    controlAnswersByControlID,
    getByAnswerID,
  };
}

export type ControlWithAnswer = { controlID: string; productID?: string; answer?: Answer };

// Responsible for the combining and sorting of controlIDs and answers - we
// always want to show every control, even if there's no answer, but sometimes
// there's more than one answer per control when there's product answers
export function useControlsWithAnswers() {
  const { ff } = useFeatureFlags();
  const { controlsByDomain, isLoading: isFrameworkLoading } = useFramework();
  const { controlAnswersByControlID, isLoading: isControlAnswersLoading } = useControlAnswers();

  const { ownProducts, isLoading: isProductsLoading } = useOwnProducts();

  const isLoading = computed(
    () => isFrameworkLoading.value || isControlAnswersLoading.value || isProductsLoading.value,
  );

  const productIDs = computed(() => ownProducts.value.map(({ id }) => id));

  // TODO: (30 minutes) we can make several performance improvements by making
  // this a map of controlIDs to arrays of productIDs
  const pendingProductIDs = ref<{ controlID: string; productID: string }[]>([]);

  const { generateControlsWithAnswers } = useGenerateControlsWithAnswers();

  function getControlsWithAnswers(controlIDs: string[] | undefined): ControlWithAnswer[] {
    if (!ff(FeatureFlag.ProductAnswers)) {
      return generateControlsWithAnswers(controlIDs, controlAnswersByControlID.value);
    }

    return generateControlsWithAnswers(
      controlIDs,
      controlAnswersByControlID.value,
      productIDs.value,
      pendingProductIDs.value,
    );
  }

  function controlsWithAnswerForDomain(domainID: string): ControlWithAnswer[] {
    return getControlsWithAnswers(controlsByDomain.value[domainID]);
  }

  function addPendingProduct(controlID: string, productID: string) {
    pendingProductIDs.value.push({
      controlID,
      productID,
    });
  }

  function pendingProductIDsForControl(controlID: string) {
    return pendingProductIDs.value
      .filter(({ controlID: id }) => id === controlID)
      .map(({ productID }) => productID);
  }

  function removePendingProductAnswer(pendingControlID: string, pendingProductID: string) {
    const pendingProductToBeRemoved = pendingProductIDs.value.findIndex(
      ({ controlID, productID }) =>
        pendingControlID === controlID && pendingProductID === productID,
    );
    if (pendingProductToBeRemoved !== -1) {
      pendingProductIDs.value.splice(pendingProductToBeRemoved, 1);
    }
  }

  return {
    isLoading,
    getControlsWithAnswers,
    controlsWithAnswerForDomain,
    addPendingProduct,
    pendingProductIDsForControl,
    removePendingProductAnswer,
    // Wrapped in computed so that it's readonly
    pendingProductIDs: computed(() => pendingProductIDs.value),
  };
}

export function useScopingComplete(
  options?: MutationObserverOptions<void, AxiosError, void, null>,
) {
  const store = useStore();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn() {
        await api.completeScopingQuestions();
      },
      async onSuccess(_data) {
        trackEvent('scoping:completed');
        // Revalidate assessment status
        await store.dispatch('assessment/getAssessmentStatus');
      },
    }),
  );
}

export function useConfirmScopingAnswer(
  options?: MutationObserverOptions<ScopingAnswer, AxiosError, string, null>,
) {
  const store = useStore();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(domainId) {
        const res = await api.confirmScopingAnswer(domainId);
        return res.data;
      },
      onSuccess(data) {
        store.commit('assessment/CHANGE_SCOPING_ANSWER', data);
      },
    }),
  );
}

export function useNewInscopeDomains(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [NEW_IN_SCOPE_DOMAINS_QUERY],
      async queryFn() {
        const res = await api.getNewInscopeDomains();
        return res.data;
      },
    }),
  );

  return { ...query, newInscopeDomains: computed(() => query.data.value || []) };
}

export function useAnswerControl(
  options?: MutationObserverOptions<
    Answer,
    AxiosError,
    { controlID: string; answer: Partial<Answer> },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ controlID, answer }) {
        const res = await api.answerControl(controlID, answer);
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueryData([ASSESSMENT_ANSWERS_QUERY], (oldData: Answer[] | undefined) => {
          if (!oldData) return undefined;
          const prevAnswerIndex = oldData.findIndex((a) => a.answerID === data.answerID);
          if (prevAnswerIndex !== -1) {
            oldData[prevAnswerIndex] = data;
          } else {
            oldData.push(data);
          }
          return oldData;
        });

        queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY] });
        queryClient.invalidateQueries({ queryKey: [ASSESSMENT_ANSWERS_QUERY] });
        queryClient.invalidateQueries({ queryKey: [CLIENT_ASSESSMENT_ANSWERS_QUERY] });
      },
    }),
  );
}

/**
 * A wrapper around useAnswerControl specifically to apply quick answer suggestions
 */
export function useApplySuggestion(
  options?: MutationObserverOptions<
    Answer,
    AxiosError,
    { controlID: string; answer: Partial<Answer> },
    null
  >,
) {
  const answerControlMutation = useAnswerControl(options);

  function mutateAsync(suggestion: QuickAnswerSuggestion) {
    return answerControlMutation.mutateAsync({
      controlID: suggestion.controlId,
      answer: {
        answer: suggestion.suggestedAnswer,
        notes: suggestion.suggestedNotes,
        documentIDs: suggestion.suggestedEvidence,
      },
    });
  }

  return { ...answerControlMutation, mutateAsync, mutate: mutateAsync };
}

export function useRemoveControlAnswer(
  options?: MutationObserverOptions<ControlAnswersResponse, AxiosError, string, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(controlID) {
        const res = await api.removeControlAnswer(controlID);
        return res.data;
      },
      onSuccess(_data) {
        queryClient.invalidateQueries({ queryKey: [ASSESSMENT_ANSWERS_QUERY] });
        queryClient.invalidateQueries({ queryKey: [CLIENT_ASSESSMENT_ANSWERS_QUERY] });
      },
    }),
  );
}

// REASSESSMENTS

export function useReassessmentStatus(options?: QueryOptions) {
  const { ff } = useFeatureFlags();
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [REASSESMENT_STATUS_QUERY],
      async queryFn() {
        const res = await api.getReassessmentStatus();
        return res.data;
      },
    }),
  );

  const { sortedDomains } = useFramework();
  const { controlsWithAnswerForDomain, isLoading: isControlsWithAnswersLoading } =
    useControlsWithAnswers();
  const { frameworkRequiredControlIDs, isLoading: isCombinedFrameworkLoading } =
    useCombinedFrameworkConfig();

  const isLoading = computed(
    () =>
      query.isLoading.value ||
      isControlsWithAnswersLoading.value ||
      isCombinedFrameworkLoading.value,
  );

  const reassessmentStatus = computed(() => query.data.value);
  const answerConfirmations = computed(() => reassessmentStatus.value?.answerConfirmations ?? []);

  const getRequiredControlsWithAnswersForDomain = (domainID: string) =>
    controlsWithAnswerForDomain(domainID).filter((c) =>
      frameworkRequiredControlIDs.value.includes(c.controlID),
    );

  const { isDomainOutOfScope } = useScopingAnswers();

  const domainsWithConfirmationStatus = computed(() =>
    Object.fromEntries(
      sortedDomains.value.map((domain) => {
        const requiredControlsWithAnswers = getRequiredControlsWithAnswersForDomain(
          domain.domainID,
        );
        const confirmedControlsWithAnswers = getConfirmedControlsWithAnswers(
          requiredControlsWithAnswers,
          answerConfirmations.value,
        );

        return [
          domain.domainID,
          {
            domain,
            percentageConfirmed: Math.floor(
              (confirmedControlsWithAnswers.length / requiredControlsWithAnswers.length) * 100,
            ),
            confirmedControlsWithAnswers,
            isConfirmed: confirmedControlsWithAnswers.length === requiredControlsWithAnswers.length,
          },
        ];
      }),
    ),
  );

  function getNumberOfConfirmedApplicableAnswersInDomain(domainID: string) {
    const requiredControlsWithAnswers = getRequiredControlsWithAnswersForDomain(domainID);
    const confirmedApplicableAnswersInDomain = requiredControlsWithAnswers.filter(
      (controlWithAnswer) =>
        isApplicableControl(controlWithAnswer) &&
        isControlConfirmed(controlWithAnswer, answerConfirmations.value),
    );

    return confirmedApplicableAnswersInDomain.length;
  }

  function getPercentageDomainConfirmed(domainID: string) {
    return domainsWithConfirmationStatus.value[domainID]?.percentageConfirmed;
  }

  function getNumberOfConfirmedAnswersInDomain(domainID: string) {
    return domainsWithConfirmationStatus.value[domainID]?.confirmedControlsWithAnswers.filter(
      (controlWithAnswer) => {
        if (!ff(FeatureFlag.ProductAnswers) && controlWithAnswer.productID) {
          return false;
        }

        return isApplicableControl(controlWithAnswer);
      },
    ).length;
  }

  function getApplicableControlsAndProductAnswersInDomain(domainID: string) {
    return getRequiredControlsWithAnswersForDomain(domainID).filter((controlWithAnswer) => {
      if (!ff(FeatureFlag.ProductAnswers) && controlWithAnswer.productID) {
        return false;
      }

      return isApplicableControl(controlWithAnswer);
    });
  }

  const unconfirmedDomains = computed(() => {
    return Object.entries(domainsWithConfirmationStatus.value)
      .filter(
        ([domainID, domainWithStatus]) =>
          !domainWithStatus.isConfirmed && !isDomainOutOfScope(domainID),
      )
      .map(([_domainID, domainWithStatus]) => domainWithStatus.domain);
  });

  const isAnswerConfirmed = (answerID: string) =>
    answerConfirmations.value.find((confirmation) => confirmation.answerID === answerID)?.confirmed;

  const isReassessing = computed(() => {
    return reassessmentStatus.value?.status === 'reassessment_inprogress';
  });

  const isReassessmentScheduled = computed(() => {
    return reassessmentStatus.value?.triggerType === ReassessmentTriggerType.ScheduledReassessment;
  });

  return {
    ...query,
    isLoading,
    getPercentageDomainConfirmed,
    getNumberOfConfirmedAnswersInDomain,
    getNumberOfConfirmedApplicableAnswersInDomain,
    getApplicableControlsAndProductAnswersInDomain,
    isAnswerConfirmed,
    reassessmentStatus,
    isReassessing,
    isReassessmentScheduled,
    domainsWithConfirmationStatus,
    unconfirmedDomains,
  };
}

export function useRequiringAttention() {
  const store = useStore();

  const { sortedControls, sortedDomains, domainWithID, controlWithID } = useFramework();

  const { frameworkRequiredDomainIDs, frameworkRequiredControlIDs } = useCombinedFrameworkConfig();

  const { isReassessing } = useReassessmentStatus();
  const { scopingAnswers, isDomainOutOfScope } = useScopingAnswers();

  const controlsRequiringAttention = computed(() => {
    const answers = store.state.assessment.controlAnswers;

    return sortedControls.value
      .filter((control) => {
        if (!frameworkRequiredControlIDs.value.includes(control.controlID)) {
          return false;
        }

        if (control.deprecated || isDomainOutOfScope(control.domainID))
          // Ignore deprecated and out of scope
          return false;

        // Add controls that changed meaning
        const answer = answers[control.controlID];
        if (answer && !answer.notApplicable && answer.unconfirmedControlUpdate) {
          return true;
        }

        // If reassessing then also add unanswered applicable controls (whose scoping question was answered)
        if (isReassessing) {
          if (
            !domainWithID(control.domainID).scopingQuestion ||
            !!scopingAnswers.value[control.domainID]
          ) {
            if (!answer || (!answer.answer && !answer.notApplicable)) {
              return true;
            }
          }
        }

        return false;
      })
      .map((control) => control.controlID);
  });

  const scopingQuestionsRequiringAttention = computed(() =>
    sortedDomains.value
      .filter((domain) => {
        if (!frameworkRequiredDomainIDs.value.includes(domain.domainID)) return false;

        // Ignore deprecated and domains without scoping questions
        if (domain.deprecated || !domain.scopingQuestion) return false;

        const answer = scopingAnswers.value[domain.domainID];
        // Ignore answered scoping questions that are confirmed
        if (answer && !answer.unconfirmedScopingControlUpdate) return false;

        return true;
      })
      .map((domain) => domain.domainID),
  );

  const totalNumberRequiringAttention = computed(
    () => controlsRequiringAttention.value.length + scopingQuestionsRequiringAttention.value.length,
  );

  const nextRequiringAttention = computed<RouteLocationRaw | undefined>(() => {
    // if a scope question requires attention, push to scoping questions
    if (scopingQuestionsRequiringAttention.value.length) {
      return {
        name: 'wizard',
        params: {
          tab: 'scoping',
        },
      };
    }
    // otherwise, push to the first domain that requires attention
    const controlID = controlsRequiringAttention.value[0];
    if (controlID) {
      const control = controlWithID(controlID);
      const domain = domainWithID(control.domainID);
      return {
        name: 'wizard',
        params: {
          tab: domain.letter,
        },
      };
    }

    return undefined;
  });

  return {
    controlsRequiringAttention,
    scopingQuestionsRequiringAttention,
    totalNumberRequiringAttention,
    nextRequiringAttention,
  };
}

export function useIncompleteReassessmentDomains() {
  const store = useStore();
  const { unconfirmedDomains } = useReassessmentStatus();

  const incompleteReassessmentDomains = computed(() => {
    const sortedIncompleteReassessmentDomains = store.getters['assessment/sortedIncompleteDomains'];
    const filteredDomains: Domain[] = [
      ...new Set([...sortedIncompleteReassessmentDomains, ...unconfirmedDomains.value]),
    ];

    // Sort filtered domains by letter
    filteredDomains.sort((a, b) => {
      if (a.letter < b.letter) {
        return -1;
      }
      if (a.letter > b.letter) {
        return 1;
      }

      return 0;
    });
    return filteredDomains;
  });

  return { incompleteReassessmentDomains };
}

export function useNextDomain(currentDomainID?: MaybeRefOrGetter<string | undefined>) {
  const { sortedDomains } = useFramework();
  const { frameworkRequiredDomainIDs } = useCombinedFrameworkConfig();
  const store = useStore();
  const { isReassessing } = useReassessmentStatus();
  const { incompleteReassessmentDomains } = useIncompleteReassessmentDomains();
  const sortedIncompleteDomains = computed<Domain[]>(() => {
    const incompleteDomains = store.getters['assessment/sortedIncompleteDomains'];
    return isReassessing.value ? incompleteReassessmentDomains.value : incompleteDomains;
  });

  return computed(() => {
    if (sortedIncompleteDomains.value.length === 0) {
      return null;
    }

    // Use domain letters when checking for order.
    const currentDomainLetter =
      sortedDomains.value.find((d) => d.domainID === toValue(currentDomainID))?.letter || '';

    const nextDomain = sortedIncompleteDomains.value.find(
      (obj) =>
        obj.letter > currentDomainLetter && frameworkRequiredDomainIDs.value.includes(obj.domainID),
    );

    return (
      nextDomain ||
      sortedIncompleteDomains.value.find((d) =>
        frameworkRequiredDomainIDs.value.includes(d.domainID),
      )
    );
  });
}

export function useSetControlAnswerConfirmation(
  options?: MutationObserverOptions<
    ConfirmationResponse,
    AxiosError,
    { answerID: string; isConfirmed: boolean },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ answerID, isConfirmed }) {
        const res = await api.setAnswerConfirmation(answerID, isConfirmed);
        return res.data;
      },
      onSuccess(newData, variables) {
        // Optimistically update the control answer confirmation status
        queryClient.setQueryData<ReassessmentStatus>([REASSESMENT_STATUS_QUERY], (oldData) => {
          if (!oldData) return undefined;

          if (oldData.answerConfirmations.find((ac) => ac.answerID === variables.answerID)) {
            return {
              ...oldData,
              answerConfirmations: oldData.answerConfirmations.map((ac) =>
                ac.answerID === variables.answerID
                  ? { ...ac, confirmed: variables.isConfirmed }
                  : ac,
              ),
            };
          }

          return {
            ...oldData,
            answerConfirmations:
              newData.reassessmentStatus.answerConfirmations ?? oldData.answerConfirmations,
          };
        });
        // We need to get the full reassessment status
        queryClient.invalidateQueries({ queryKey: [REASSESMENT_STATUS_QUERY] });
      },
    }),
  );
}

export function useConfirmDomain(
  options?: MutationObserverOptions<ConfirmationResponse, AxiosError, { domainID: string }, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ domainID }) {
        const res = await api.confirmDomain(domainID);
        return res.data;
      },
      onSuccess(newData) {
        // Optimistically update the domain confirmation status
        queryClient.setQueryData<ReassessmentStatus>([REASSESMENT_STATUS_QUERY], (oldData) => {
          if (!oldData) return undefined;

          return {
            ...oldData,
            answerConfirmations:
              newData.reassessmentStatus.answerConfirmations ?? oldData.answerConfirmations,
          };
        });
        // We need to get the full reassessment status
        queryClient.invalidateQueries({ queryKey: [REASSESMENT_STATUS_QUERY] });
      },
    }),
  );
}

export function useArchiveProductAnswer(
  options?: MutationObserverOptions<
    null,
    AxiosError,
    { productID: string; answerID: string },
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ productID, answerID }) {
        const res = await api.archiveProductAnswer(productID, answerID);
        return res.data;
      },
      onSuccess(_data) {
        queryClient.invalidateQueries({ queryKey: [ASSESSMENT_ANSWERS_QUERY] });
        queryClient.invalidateQueries({ queryKey: [CLIENT_ASSESSMENT_ANSWERS_QUERY] });
      },
    }),
  );
}

export function useStartReassessment(
  options?: MutationObserverOptions<ReassessmentStatus, AxiosError>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn() {
        const res = await api.startReassessment();
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueryData<ReassessmentStatus>([REASSESMENT_STATUS_QUERY], data);

        queryClient.invalidateQueries({ queryKey: [REASSESMENT_STATUS_QUERY] });
      },
    }),
  );
}
